import { css } from 'lit';

/**
 * The mock's component CSS, split into the pieces each shadow root composes. Colours, radii,
 * shadows and the typeface come from the :root tokens in theme.css and inherit through the
 * shadow boundary; nothing here hard-codes a brand value that theme.css does not own.
 */

export const base = css`
  *, *::before, *::after { box-sizing: border-box; }
  :host {
    font-family: var(--pbg-font-body);
    font-size: 13.5px;
    line-height: 1.5;
    color: var(--pbg-ink);
  }
  button, input, select, textarea { font-family: inherit; }
  .sr-only {
    position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px;
    overflow: hidden; clip: rect(0, 0, 0, 0); border: 0;
  }
  .mono { font-variant-numeric: tabular-nums; }
`;

export const buttons = css`
  .btn {
    appearance: none; border: 1px solid transparent; cursor: pointer;
    font-size: 13px; font-weight: 600; line-height: 1;
    padding: 10px 16px; border-radius: var(--pbg-radius-sm);
    display: inline-flex; align-items: center; justify-content: center; gap: 8px;
    transition: background .14s, border-color .14s, color .14s, box-shadow .14s, transform .04s;
    white-space: nowrap; text-decoration: none;
  }
  .btn:active { transform: translateY(.5px); }
  .btn:focus-visible { outline: 2px solid var(--pbg-blue); outline-offset: 2px; }
  .btn-primary { background: var(--pbg-navy); color: #fff; }
  .btn-primary:hover { background: #0f2c5c; }
  .btn-secondary { background: var(--pbg-surface); color: var(--pbg-navy); border-color: var(--pbg-gray-200); }
  .btn-secondary:hover { background: var(--pbg-blue-tint); border-color: #c9d7e8; }
  .btn-ghost { background: transparent; color: var(--pbg-gray-700); }
  .btn-ghost:hover { background: #eef1f6; color: var(--pbg-ink); }
  .btn-sm { padding: 7px 11px; font-size: 12px; }
  .btn[disabled] { opacity: .55; cursor: not-allowed; }
  .btn[disabled]:active { transform: none; }
  .btn .spin {
    width: 14px; height: 14px; border-radius: 50%;
    border: 2px solid rgba(255, 255, 255, .45); border-top-color: #fff;
    animation: spin .7s linear infinite;
  }
  .btn-secondary .spin { border-color: rgba(31, 95, 168, .3); border-top-color: var(--pbg-blue); }
  .actions { margin-top: 18px; display: flex; gap: 10px; flex-wrap: wrap; align-items: center; }
  @keyframes spin { to { transform: rotate(360deg); } }
`;

export const screen = css`
  .scr { animation: scrIn .32s cubic-bezier(.2, .7, .2, 1); }
  @keyframes scrIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: none; } }
  @media (prefers-reduced-motion: reduce) { .scr { animation: none; } }

  .scr-head { display: flex; align-items: flex-start; gap: 12px; margin-bottom: 16px; }
  .scr-head .scr-title {
    margin: 0; font-family: var(--pbg-font-head); font-size: 19px; font-weight: 700;
    color: var(--pbg-navy); letter-spacing: -.01em; outline: none;
  }
  .scr-head .scr-sub { margin: 4px 0 0; font-size: 13px; color: var(--pbg-gray-700); line-height: 1.45; }
  @media (max-width: 480px) { .scr-head .scr-title { font-size: 17px; } }
`;

export const cards = css`
  .card {
    background: var(--pbg-surface); border: 1px solid var(--pbg-gray-200);
    border-radius: var(--pbg-radius); box-shadow: var(--pbg-shadow-sm);
  }
  .card + .card { margin-top: var(--pbg-space-gap); }
  .card-head {
    padding: 12px 16px; border-bottom: 1px solid var(--pbg-gray-200);
    display: flex; align-items: center; gap: 8px;
  }
  .card-head .ch-title {
    font-size: 12px; font-weight: 700; letter-spacing: .04em; text-transform: uppercase;
    color: var(--pbg-gray-700);
  }
  .card-head .ch-spacer { flex: 1; }
  .ro-tag {
    font-size: 10px; font-weight: 700; letter-spacing: .05em; text-transform: uppercase;
    color: var(--pbg-gray-400); display: inline-flex; align-items: center; gap: 5px;
  }
  .ro-tag::before {
    content: ""; width: 11px; height: 11px; border-radius: 3px; border: 1.5px solid var(--pbg-gray-400);
    background: linear-gradient(var(--pbg-gray-400), var(--pbg-gray-400)) no-repeat center / 5px 5px;
  }
  .ro-tag.count { color: var(--pbg-blue); }
  .ro-tag.count::before { border-color: var(--pbg-blue); background-image: linear-gradient(var(--pbg-blue), var(--pbg-blue)); }
  .card-body { padding: var(--pbg-space-card); }

  /* read-only summary grid (loan data -- NEVER editable, cert 1.5/1.6) */
  .summary-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 14px 22px; }
  @media (max-width: 480px) { .summary-grid { grid-template-columns: 1fr; gap: 12px; } }
  .field label, .field .lbl {
    display: block; font-size: 11px; font-weight: 600; letter-spacing: .03em; text-transform: uppercase;
    color: var(--pbg-gray-400); margin-bottom: 3px;
  }
  .field .val { font-size: 14px; color: var(--pbg-ink); font-weight: 500; overflow-wrap: anywhere; }
  .field .val.mono { font-variant-numeric: tabular-nums; }
  .field .val.big { font-size: 16px; font-weight: 600; }
  .field.span { grid-column: 1 / -1; }
  .field .val.empty { color: var(--pbg-gray-400); }

  .divider { height: 1px; background: var(--pbg-gray-200); margin: 14px 0; border: 0; }
  .field-rows { display: flex; flex-direction: column; gap: 11px; }
  .frow { display: flex; justify-content: space-between; gap: 16px; font-size: 13px; }
  .frow .k { color: var(--pbg-gray-700); }
  .frow .v { color: var(--pbg-ink); font-weight: 600; text-align: right; overflow-wrap: anywhere; }
`;

export const callouts = css`
  .callout {
    display: flex; gap: 11px; padding: 13px 15px; border-radius: var(--pbg-radius);
    font-size: 12.5px; line-height: 1.5;
  }
  .callout .ico {
    flex: 0 0 auto; width: 20px; height: 20px; border-radius: 50%; display: flex; align-items: center;
    justify-content: center; font-size: 12px; font-weight: 800; color: #fff;
  }
  .callout.info { background: var(--pbg-blue-tint); color: #234c7e; }
  .callout.info .ico { background: var(--pbg-blue); }
  .callout.success { background: var(--pbg-success-tint); color: #15583a; }
  .callout.success .ico { background: var(--pbg-success); }
  .callout.warn { background: var(--pbg-warning-tint); color: #7a4a00; }
  .callout.warn .ico { background: var(--pbg-warning); }
  .callout.danger { background: var(--pbg-danger-tint); color: #8a201b; }
  .callout.danger .ico { background: var(--pbg-danger); }
`;

export const pills = css`
  .status-pill {
    display: inline-flex; align-items: center; gap: 7px; font-size: 12px; font-weight: 700;
    padding: 5px 11px; border-radius: var(--pbg-radius-pill); letter-spacing: .02em; white-space: nowrap;
  }
  .status-pill .sd { width: 8px; height: 8px; border-radius: 50%; flex: 0 0 auto; }
  .status-pill.processing { background: var(--pbg-blue-tint); color: #1d4e86; }
  .status-pill.processing .sd { background: var(--pbg-blue); animation: pulse 1.2s ease-in-out infinite; }
  .status-pill.completed { background: var(--pbg-success-tint); color: #15583a; }
  .status-pill.completed .sd { background: var(--pbg-success); }
  .status-pill.failed { background: var(--pbg-danger-tint); color: #8a201b; }
  .status-pill.failed .sd { background: var(--pbg-danger); }
  .status-pill.warn { background: var(--pbg-warning-tint); color: #7a4a00; }
  .status-pill.warn .sd { background: var(--pbg-warning); }
  .status-pill.neutral { background: #eef1f6; color: var(--pbg-gray-700); }
  .status-pill.neutral .sd { background: var(--pbg-gray-400); }
  .status-pill.cancelled { background: #eef1f6; color: var(--pbg-gray-700); }
  .status-pill.cancelled .sd { background: var(--pbg-gray-400); }
  @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: .3; } }
  @media (prefers-reduced-motion: reduce) { .status-pill .sd { animation: none; } }
`;

export const forms = css`
  .form-row { margin-bottom: 16px; }
  .form-row:last-child { margin-bottom: 0; }
  .form-row > label.flbl, .form-row > .flbl {
    display: block; font-size: 12px; font-weight: 600; color: var(--pbg-ink); margin-bottom: 7px;
  }
  .req { color: var(--pbg-danger); }
  select.inp, input.inp, textarea.inp {
    width: 100%; font-family: inherit; font-size: 13.5px; color: var(--pbg-ink);
    padding: 10px 12px; border: 1px solid var(--pbg-gray-200); border-radius: var(--pbg-radius-sm);
    background: var(--pbg-surface); transition: border-color .14s, box-shadow .14s;
  }
  select.inp:focus, input.inp:focus, textarea.inp:focus {
    outline: none; border-color: var(--pbg-blue); box-shadow: 0 0 0 3px rgba(31, 95, 168, .15);
  }
  textarea.inp { resize: vertical; min-height: 64px; line-height: 1.5; }
  .err-msg { display: none; font-size: 11.5px; color: var(--pbg-danger); margin-top: 5px; font-weight: 500; }
  .err-msg.show { display: block; }

  /* segmented radio */
  .seg {
    display: inline-flex; background: #eef1f6; border-radius: var(--pbg-radius-sm); padding: 3px; gap: 3px;
    flex-wrap: wrap; position: relative;
  }
  .seg label {
    font-size: 12.5px; font-weight: 600; color: var(--pbg-gray-700);
    padding: 7px 13px; border-radius: 5px; cursor: pointer; transition: all .12s; user-select: none;
  }
  .seg input { position: absolute; opacity: 0; pointer-events: none; }
  .seg input:checked + label { background: var(--pbg-surface); color: var(--pbg-navy); box-shadow: var(--pbg-shadow-sm); }
  .seg input:focus-visible + label { outline: 2px solid var(--pbg-blue); outline-offset: 1px; }

  /* checkbox rows (documents, rush) */
  .doc-list { display: flex; flex-direction: column; gap: 8px; }
  .doc-item {
    display: flex; align-items: flex-start; gap: 12px; padding: 12px 14px;
    border: 1px solid var(--pbg-gray-200); border-radius: var(--pbg-radius-sm); background: var(--pbg-surface);
    cursor: pointer; transition: border-color .12s, background .12s;
  }
  .doc-item.dashed { border-style: dashed; }
  .doc-item:hover { border-color: #c9d7e8; background: var(--pbg-blue-tint); }
  .doc-item.on { border-color: var(--pbg-blue); background: var(--pbg-blue-tint); }
  .doc-item:focus-within { outline: 2px solid var(--pbg-blue); outline-offset: 1px; }
  .doc-item input { margin: 2px 0 0; width: 17px; height: 17px; accent-color: var(--pbg-blue); flex: 0 0 auto; }
  .doc-meta { min-width: 0; }
  .doc-meta .dname { display: block; font-size: 13.5px; font-weight: 600; color: var(--pbg-ink); overflow-wrap: anywhere; }
  .doc-meta .dsub { display: block; font-size: 11.5px; color: var(--pbg-gray-700); margin-top: 2px; }
  .doc-meta .efolder {
    display: inline-flex; align-items: center; gap: 5px; margin-top: 6px;
    font-size: 11px; color: var(--pbg-blue); background: var(--pbg-surface);
    border: 1px solid #d6e2f1; padding: 2px 8px; border-radius: var(--pbg-radius-pill); font-weight: 600;
  }
  .doc-meta .efolder::before { content: "\\2913"; font-size: 12px; }
`;

export const centerState = css`
  .center-state {
    display: flex; flex-direction: column; align-items: center; justify-content: center;
    text-align: center; min-height: 380px; padding: 24px;
  }
  .cs-ico {
    width: 64px; height: 64px; border-radius: 50%; display: flex; align-items: center; justify-content: center;
    margin-bottom: 18px; font-size: 28px;
  }
  .cs-ico.blue { background: var(--pbg-blue-tint); color: var(--pbg-blue); }
  .cs-ico.success { background: var(--pbg-success-tint); color: var(--pbg-success); }
  .cs-ico.danger { background: var(--pbg-danger-tint); color: var(--pbg-danger); }
  .cs-ico.warn { background: var(--pbg-warning-tint); color: var(--pbg-warning); }
  .cs-ico.neutral { background: #eef1f6; color: var(--pbg-gray-700); }
  .center-state h2 {
    font-family: var(--pbg-font-head); font-size: 20px; font-weight: 700; color: var(--pbg-navy);
    margin: 0 0 8px; outline: none;
  }
  .center-state p { font-size: 13.5px; color: var(--pbg-gray-700); line-height: 1.55; max-width: 380px; margin: 0 0 4px; }
  .center-state .cs-actions { display: flex; gap: 10px; margin-top: 22px; flex-wrap: wrap; justify-content: center; }
  .center-state .reference { font-size: 11.5px; color: var(--pbg-gray-400); margin-top: 16px; font-variant-numeric: tabular-nums; }

  .big-spin {
    width: 46px; height: 46px; border-radius: 50%; border: 4px solid var(--pbg-gray-200);
    border-top-color: var(--pbg-blue); animation: spin .8s linear infinite; margin-bottom: 20px;
  }
  @keyframes spin { to { transform: rotate(360deg); } }
  @media (prefers-reduced-motion: reduce) { .big-spin { animation-duration: 2s; } }
`;
